import React, { useState, useEffect, useCallback } from 'react'
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, CircularProgress, IconButton, Tooltip, TextField
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import hipersendService from '../../services/hipersend'
import Text from '../Text'

const sf = hipersendService.smartFilter

function fmtDate(d) {
  if (!d) return '—'
  const dt = new Date(d)
  return isNaN(dt) ? '—' : dt.toLocaleDateString('pt-BR')
}

// Meses cheios de atraso entre a data mais antiga e hoje (só p/ exibição).
function monthsAgo(d) {
  if (!d) return null
  const dt = new Date(d)
  if (isNaN(dt)) return null
  const now = new Date()
  let m = (now.getFullYear() - dt.getFullYear()) * 12 + (now.getMonth() - dt.getMonth())
  if (now.getDate() < dt.getDate()) m -= 1
  return m > 0 ? m : 0
}

export default function SilencedCustomers({ api, secret, settings }) {
  const defMonths = (settings && Number(settings.overdueMonths)) || 6
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [months, setMonths] = useState(defMonths)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await sf.getDelinquency(api, secret, { months: Number(months) || 6, page: page + 1, limit: rowsPerPage })
      setRows((r && r.data) || [])
      setTotal((r && r.total) || 0)
    } catch (e) {
      setRows([]); setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [api, secret, months, page, rowsPerPage])

  useEffect(() => { setPage(0) }, [months])
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t) }, [load])

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <TextField
          size="small" type="number" label="A partir de (meses de atraso)"
          value={months} onChange={(e) => setMonths(e.target.value)} style={{ width: 220 }}
          inputProps={{ min: 1 }}
        />
        <Tooltip title="Atualizar">
          <span><IconButton size="small" onClick={load} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></span>
        </Tooltip>
      </div>

      <Text className="block text-xs opacity-60">
        Clientes com título em aberto mais antigo acima do limite — todas as mensagens a eles são silenciadas
        (reativação/desbloqueio continuam passando). O histórico é reconstruído pelas cobranças que passam pelo filtro.
      </Text>

      {loading ? (
        <div className="flex justify-center py-6"><CircularProgress size={24} /></div>
      ) : !rows.length ? (
        <Text className="block text-sm opacity-60 py-4">
          Nenhum cliente silenciado com esse limite. Os registros aparecem conforme as cobranças passam pelo filtro.
        </Text>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '16px' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cliente (telefone)</TableCell>
                <TableCell>Título mais antigo</TableCell>
                <TableCell align="right">Atraso</TableCell>
                <TableCell align="right">Títulos em aberto</TableCell>
                <TableCell style={{ whiteSpace: 'nowrap' }}>Visto por último</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const m = monthsAgo(r.oldestOpenDueDate)
                const lastSeen = (r.openTitles || []).reduce((acc, t) => {
                  const ts = t.lastSeenAt ? new Date(t.lastSeenAt).getTime() : 0
                  return ts > acc ? ts : acc
                }, 0)
                return (
                  <TableRow key={r._id} hover>
                    <TableCell style={{ fontFamily: 'ui-monospace, monospace' }}>{r.recipientKey || '—'}</TableCell>
                    <TableCell>{fmtDate(r.oldestOpenDueDate)}</TableCell>
                    <TableCell align="right">{m != null ? `${m} mês(es)` : '—'}</TableCell>
                    <TableCell align="right">{(r.openTitles || []).length}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{lastSeen ? fmtDate(lastSeen) : '—'}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
            rowsPerPageOptions={[20, 50, 100]}
            labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </TableContainer>
      )}
    </div>
  )
}
